/**
 * theme.js — Summit global namespace, event bus, and Cart API
 * All cross-component communication goes through document-level CustomEvents.
 * No localStorage — all state is in-memory or server-sourced.
 */

window.Summit = {
  version: '1.0.0',

  events: {
    CART_OPEN: 'cart:open',
    CART_CLOSE: 'cart:close',
    CART_ADD: 'cart:add',
    CART_UPDATED: 'cart:updated',
    CART_ERROR: 'cart:error',
    CART_RENDERED: 'cart:rendered',
    SIDEBAR_OPEN: 'sidebar:open',
    SIDEBAR_CLOSE: 'sidebar:close',
    COUNTDOWN_EXPIRED: 'countdown:expired',
    QUANTITY_CHANGE: 'quantity:change',
    SEARCH_OPEN: 'search:open',
    SEARCH_CLOSE: 'search:close',
    TOAST_SHOW: 'toast:show',
  },

  cart: null,

  features: {
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    touch: 'ontouchstart' in window,
  },

  dispatch(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }));
  },

  async fetchJSON(url, options = {}) {
    const defaults = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
    const response = await fetch(url, { ...defaults, ...options, headers: { ...defaults.headers, ...(options.headers || {}) } });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ description: response.statusText }));
      throw new Error(error.description || error.message || `HTTP ${response.status}`);
    }
    return response.json();
  },

  cartAPI: {
    async add(items) {
      const payload = Array.isArray(items) ? { items } : { items: [items] };
      const cart = await Summit.fetchJSON('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      Summit.cart = await Summit.cartAPI.get();
      Summit.dispatch(Summit.events.CART_UPDATED, { cart: Summit.cart });
      return cart;
    },

    async update(updates) {
      const cart = await Summit.fetchJSON('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
      Summit.cart = cart;
      Summit.dispatch(Summit.events.CART_UPDATED, { cart });
      return cart;
    },

    async change(payload) {
      const cart = await Summit.fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      Summit.cart = cart;
      Summit.dispatch(Summit.events.CART_UPDATED, { cart });
      return cart;
    },

    async get() {
      const cart = await Summit.fetchJSON('/cart.js');
      Summit.cart = cart;
      return cart;
    },
  },

  toast: {
    show(message, type = 'info', duration = 3000) {
      Summit.dispatch(Summit.events.TOAST_SHOW, { message, type, duration });
    },
  },

  formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    return (format || '₹{{ amount }}').replace('{{ amount }}', amount).replace('{{amount}}', amount);
  },
};

/* Wire cart-bubble button to open cart drawer */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-cart-trigger]').forEach((btn) => {
    btn.addEventListener('click', () => Summit.dispatch(Summit.events.CART_OPEN));
  });

  document.querySelectorAll('[data-sidebar-trigger]').forEach((btn) => {
    btn.addEventListener('click', () => Summit.dispatch(Summit.events.SIDEBAR_OPEN));
  });

  document.querySelectorAll('[data-search-trigger]').forEach((btn) => {
    btn.addEventListener('click', () => Summit.dispatch(Summit.events.SEARCH_OPEN));
  });
});

/* Update cart count bubbles + floating bubble visibility whenever cart changes */
document.addEventListener('cart:updated', (event) => {
  const cart = event.detail?.cart;
  if (!cart) return;

  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
    el.hidden = cart.item_count === 0;
  });

  const cartBubble = document.querySelector('[data-cart-bubble]');
  if (cartBubble) {
    const wasHidden = cartBubble.hidden;
    cartBubble.hidden = cart.item_count === 0;
    // Pop-in animation only when transitioning from empty → has items
    if (wasHidden && cart.item_count > 0) {
      cartBubble.classList.remove('cart-bubble--pop-in');
      // Force reflow so the animation restarts if triggered multiple times
      void cartBubble.offsetWidth;
      cartBubble.classList.add('cart-bubble--pop-in');
      cartBubble.addEventListener('animationend', () => {
        cartBubble.classList.remove('cart-bubble--pop-in');
      }, { once: true });
    }
  }
});
